/**
 * Error response format from API
 */
interface ApiErrorResponse {
  message?: string
  errors?: Record<string, string[]>
}

/**
 * Extracts error message from API error response
 * Handles both simple error objects and validation error responses
 */
export function getErrorMessage(error: any): string {
  // Check if it's an Axios error with response data
  if (error?.response?.data) {
    const errorData: ApiErrorResponse = error.response.data
    
    // If there's a main message, use it
    if (errorData.message) {
      // If there are also field-specific errors, append them
      if (errorData.errors && Object.keys(errorData.errors).length > 0) {
        const fieldErrors = Object.entries(errorData.errors)
          .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
          .join('; ')
        return `${errorData.message} ${fieldErrors}`
      }
      return errorData.message
    }
    
    // If no main message but there are field errors
    if (errorData.errors && Object.keys(errorData.errors).length > 0) {
      const errorMessages: string[] = []
      Object.entries(errorData.errors).forEach(([field, messages]) => {
        messages.forEach(msg => errorMessages.push(msg))
      })
      return errorMessages.join('. ')
    }
  }
  
  // Fallback to error message or default
  return error?.message || error?.toString() || 'An unexpected error occurred'
}

/**
 * Formats error response for toast notification
 * Returns title, description, and error list for better UX
 */
export function formatErrorForToast(error: any): { 
  title: string
  description: string
  errors?: string[]
} {
  if (error?.response?.data) {
    const errorData: ApiErrorResponse = error.response.data
    
    // If there are field-specific errors, display each one separately
    if (errorData.errors && Object.keys(errorData.errors).length > 0) {
      const allErrors: string[] = []
      Object.entries(errorData.errors).forEach(([field, messages]) => {
        messages.forEach(msg => allErrors.push(msg))
      })
      
      return {
        title: errorData.message || 'Validation Error',
        description: '', // Empty description when we have error list
        errors: allErrors
      }
    }
    
    // If only main message exists
    if (errorData.message) {
      return {
        title: 'Error',
        description: errorData.message
      }
    }
  }
  
  // Fallback
  const message = error?.message || error?.toString() || 'An unexpected error occurred'
  return {
    title: 'Error',
    description: message
  }
}

